import React from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Grid,
    Divider,
    List,
    ListItem,
    ListItemText,
    IconButton
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import BoltPatternDiagram from './BoltPatternDiagram';

const GeneralTorqueProcedure = () => {
    const navigate = useNavigate();

    // Bolt tightening sequences
    const boltSequences = [
        { bolts: 4, sequence: '1,3,2,4' },
        { bolts: 8, sequence: '1,5,3,7,2,6,4,8' },
        { bolts: 12, sequence: '1,7,4,10,2,8,5,11,3,9,6,12' },
        { bolts: 16, sequence: '1,9,5,13,3,11,7,15,2,10,6,14,4,12,8,16' },
        { bolts: 20, sequence: '1,11,6,16,3,13,8,18,5,15,10,20,2,12,7,17,4,14,9,19' },
        { bolts: 24, sequence: '1,13,7,19,4,16,10,22,2,14,8,20,5,17,11,23,6,18,12,24,3,15,9,21' },
        { bolts: 28, sequence: '1,15,8,22,4,18,11,25,6,20,13,27,2,16,9,23,5,19,12,26,3,17,10,24,7,21,14,28' },
        { bolts: 32, sequence: '1,17,9,25,5,21,13,29,3,19,11,27,7,23,15,31,2,18,10,26,6,22,14,30,8,24,16,32,4,20,12,28' }
    ];

    // Torque data for Class 150
    const class150Data = [
        { sizeInch: '1/2"', sizeMm: 15, bolts: 4, boltDia: '½"', thread: 'UNC', stress: 25000, torqueFt: 22, torqueNm: 30 },
        { sizeInch: '3/4"', sizeMm: 20, bolts: 4, boltDia: '½"', thread: 'UNC', stress: 30000, torqueFt: 26, torqueNm: 36 },
        { sizeInch: '1', sizeMm: 35, bolts: 4, boltDia: '½"', thread: 'UNC', stress: 33000, torqueFt: 29, torqueNm: 39 },
        { sizeInch: '1 ½', sizeMm: 40, bolts: 4, boltDia: '½"', thread: 'UNC', stress: 50000, torqueFt: 44, torqueNm: 60 },
        { sizeInch: '2', sizeMm: 50, bolts: 4, boltDia: '5/8"', thread: 'UNC', stress: 50000, torqueFt: 86, torqueNm: 117 },
        { sizeInch: '3', sizeMm: 80, bolts: 4, boltDia: '5/8"', thread: 'UNC', stress: 50000, torqueFt: 86, torqueNm: 117 },
        { sizeInch: '4', sizeMm: 100, bolts: 8, boltDia: '5/8"', thread: 'UNC', stress: 40000, torqueFt: 69, torqueNm: 93 },
        { sizeInch: '6', sizeMm: 150, bolts: 8, boltDia: '¾"', thread: 'UNC', stress: 45000, torqueFt: 137, torqueNm: 185 },
        { sizeInch: '8', sizeMm: 200, bolts: 8, boltDia: '¾"', thread: 'UNC', stress: 45000, torqueFt: 137, torqueNm: 185 },
        { sizeInch: '10', sizeMm: 250, bolts: 12, boltDia: '7/8"', thread: 'UNC', stress: 40000, torqueFt: 194, torqueNm: 263 },
        { sizeInch: '12', sizeMm: 300, bolts: 12, boltDia: '7/8"', thread: 'UNC', stress: 40000, torqueFt: 194, torqueNm: 263 },
        { sizeInch: '14', sizeMm: 350, bolts: 12, boltDia: '1"', thread: 'UN8', stress: 41000, torqueFt: 296, torqueNm: 401 },
        { sizeInch: '16', sizeMm: 400, bolts: 16, boltDia: '1"', thread: 'UN8', stress: 40000, torqueFt: 289, torqueNm: 392 },
        { sizeInch: '18', sizeMm: 450, bolts: 16, boltDia: '1 1/8"', thread: 'UN8', stress: 40000, torqueFt: 421, torqueNm: 571 },
        { sizeInch: '20', sizeMm: 500, bolts: 20, boltDia: '1 1/8"', thread: 'UN8', stress: 40000, torqueFt: 421, torqueNm: 571 },
        { sizeInch: '24', sizeMm: 600, bolts: 20, boltDia: '1 ¼"', thread: 'UN8', stress: 40000, torqueFt: 588, torqueNm: 797 }
    ];

    // Torque data for Class 300
    const class300Data = [
        { sizeInch: '1/2"', sizeMm: 15, bolts: 4, boltDia: '½"', thread: 'UNC', stress: 40000, torqueFt: 35, torqueNm: 47 },
        { sizeInch: '3/4"', sizeMm: 20, bolts: 4, boltDia: '½"', thread: 'UNC', stress: 40000, torqueFt: 69, torqueNm: 93 },
        { sizeInch: '1', sizeMm: 35, bolts: 4, boltDia: '½"', thread: 'UNC', stress: 40000, torqueFt: 69, torqueNm: 93 },
        { sizeInch: '1 ½', sizeMm: 40, bolts: 4, boltDia: '½"', thread: 'UNC', stress: 31000, torqueFt: 94, torqueNm: 128 },
        { sizeInch: '2', sizeMm: 50, bolts: 4, boltDia: '5/8"', thread: 'UNC', stress: 37000, torqueFt: 64, torqueNm: 87 },
        { sizeInch: '3', sizeMm: 80, bolts: 4, boltDia: '5/8"', thread: 'UNC', stress: 42000, torqueFt: 127, torqueNm: 173 },
        { sizeInch: '4', sizeMm: 100, bolts: 8, boltDia: '5/8"', thread: 'UNC', stress: 45000, torqueFt: 137, torqueNm: 185 },
        { sizeInch: '6', sizeMm: 150, bolts: 8, boltDia: '¾"', thread: 'UNC', stress: 45000, torqueFt: 137, torqueNm: 185 },
        { sizeInch: '8', sizeMm: 200, bolts: 8, boltDia: '¾"', thread: 'UNC', stress: 45000, torqueFt: 218, torqueNm: 296 },
        { sizeInch: '10', sizeMm: 250, bolts: 12, boltDia: '7/8"', thread: 'UNC', stress: 45000, torqueFt: 325, torqueNm: 441 },
        { sizeInch: '12', sizeMm: 300, bolts: 12, boltDia: '7/8"', thread: 'UNC', stress: 43000, torqueFt: 453, torqueNm: 614 },
        { sizeInch: '14', sizeMm: 350, bolts: 12, boltDia: '1"', thread: 'UN8', stress: 45000, torqueFt: 474, torqueNm: 643 },
        { sizeInch: '16', sizeMm: 400, bolts: 16, boltDia: '1"', thread: 'UN8', stress: 31000, torqueFt: 456, torqueNm: 618 },
        { sizeInch: '18', sizeMm: 450, bolts: 16, boltDia: '1 1/8"', thread: 'UN8', stress: 34000, torqueFt: 500, torqueNm: 678 },
        { sizeInch: '20', sizeMm: 500, bolts: 20, boltDia: '1 1/8"', thread: 'UN8', stress: 40000, torqueFt: 588, torqueNm: 797 },
        { sizeInch: '24', sizeMm: 600, bolts: 20, boltDia: '1 ¼"', thread: 'UN8', stress: 31000, torqueFt: 809, torqueNm: 1097 }
    ];

    // Torque data for Class 600
    const class600Data = [
        { sizeInch: '1/2"', sizeMm: 15, bolts: 4, boltDia: '½"', thread: 'UNC', stress: 40000, torqueFt: 35, torqueNm: 48 },
        { sizeInch: '3/4"', sizeMm: 20, bolts: 4, boltDia: '½"', thread: 'UNC', stress: 40000, torqueFt: 69, torqueNm: 94 },
        { sizeInch: '1', sizeMm: 35, bolts: 4, boltDia: '½"', thread: 'UNC', stress: 40000, torqueFt: 69, torqueNm: 94 },
        { sizeInch: '1 ½', sizeMm: 40, bolts: 4, boltDia: '½"', thread: 'UNC', stress: 45000, torqueFt: 137, torqueNm: 185 },
        { sizeInch: '2', sizeMm: 50, bolts: 4, boltDia: '5/8"', thread: 'UNC', stress: 50000, torqueFt: 86, torqueNm: 117 },
        { sizeInch: '3', sizeMm: 80, bolts: 4, boltDia: '5/8"', thread: 'UNC', stress: 45000, torqueFt: 137, torqueNm: 185 },
        { sizeInch: '4', sizeMm: 100, bolts: 8, boltDia: '5/8"', thread: 'UNC', stress: 45000, torqueFt: 218, torqueNm: 296 },
        { sizeInch: '6', sizeMm: 150, bolts: 8, boltDia: '¾"', thread: 'UNC', stress: 45000, torqueFt: 325, torqueNm: 441 },
        { sizeInch: '8', sizeMm: 200, bolts: 8, boltDia: '¾"', thread: 'UNC', stress: 50000, torqueFt: 526, torqueNm: 714 },
        { sizeInch: '10', sizeMm: 250, bolts: 12, boltDia: '7/8"', thread: 'UNC', stress: 45000, torqueFt: 662, torqueNm: 897 },
        { sizeInch: '12', sizeMm: 300, bolts: 12, boltDia: '7/8"', thread: 'UNC', stress: 45000, torqueFt: 662, torqueNm: 897 },
        { sizeInch: '14', sizeMm: 350, bolts: 12, boltDia: '1"', thread: 'UN8', stress: 45000, torqueFt: 894, torqueNm: 1213 },
        { sizeInch: '16', sizeMm: 400, bolts: 16, boltDia: '1"', thread: 'UN8', stress: 45000, torqueFt: 1175, torqueNm: 1593 },
        { sizeInch: '18', sizeMm: 450, bolts: 16, boltDia: '1 1/8"', thread: 'UN8', stress: 45000, torqueFt: 1507, torqueNm: 2044 },
        { sizeInch: '20', sizeMm: 500, bolts: 20, boltDia: '1 1/8"', thread: 'UN8', stress: 45000, torqueFt: 1507, torqueNm: 2044 },
        { sizeInch: '24', sizeMm: 600, bolts: 20, boltDia: '1 ¼"', thread: 'UN8', stress: 45000, torqueFt: 2354, torqueNm: 3191 }
    ];

    // Torque data for Class 900
    const class900Data = [
        { sizeInch: '1/2"', sizeMm: 15, bolts: 4, boltDia: '½"', thread: 'UNC', stress: 40000, torqueFt: 121, torqueNm: 165 },
        { sizeInch: '3/4"', sizeMm: 20, bolts: 4, boltDia: '½"', thread: 'UNC', stress: 40000, torqueFt: 121, torqueNm: 165 },
        { sizeInch: '1', sizeMm: 35, bolts: 4, boltDia: '½"', thread: 'UNC', stress: 40000, torqueFt: 194, torqueNm: 263 },
        { sizeInch: '1 ½', sizeMm: 40, bolts: 4, boltDia: '½"', thread: 'UNC', stress: 45000, torqueFt: 325, torqueNm: 441 },
        { sizeInch: '2', sizeMm: 50, bolts: 4, boltDia: '5/8"', thread: 'UNC', stress: 40000, torqueFt: 194, torqueNm: 263 },
        { sizeInch: '3', sizeMm: 80, bolts: 4, boltDia: '5/8"', thread: 'UNC', stress: 50000, torqueFt: 243, torqueNm: 329 },
        { sizeInch: '4', sizeMm: 100, bolts: 8, boltDia: '5/8"', thread: 'UNC', stress: 45000, torqueFt: 474, torqueNm: 642 },
        { sizeInch: '6', sizeMm: 150, bolts: 8, boltDia: '¾"', thread: 'UNC', stress: 50000, torqueFt: 526, torqueNm: 714 },
        { sizeInch: '8', sizeMm: 200, bolts: 8, boltDia: '¾"', thread: 'UNC', stress: 45000, torqueFt: 894, torqueNm: 1213 },
        { sizeInch: '10', sizeMm: 250, bolts: 12, boltDia: '7/8"', thread: 'UNC', stress: 50000, torqueFt: 994, torqueNm: 1347 },
        { sizeInch: '12', sizeMm: 300, bolts: 12, boltDia: '7/8"', thread: 'UNC', stress: 50000, torqueFt: 994, torqueNm: 1347 },
        { sizeInch: '14', sizeMm: 350, bolts: 12, boltDia: '1"', thread: 'UN8', stress: 50000, torqueFt: 1306, torqueNm: 1770 },
        { sizeInch: '16', sizeMm: 400, bolts: 16, boltDia: '1"', thread: 'UN8', stress: 50000, torqueFt: 1675, torqueNm: 2271 },
        { sizeInch: '18', sizeMm: 450, bolts: 16, boltDia: '1 1/8"', thread: 'UN8', stress: 50000, torqueFt: 2615, torqueNm: 3545 },
        { sizeInch: '20', sizeMm: 500, bolts: 20, boltDia: '1 1/8"', thread: 'UN8', stress: 50000, torqueFt: 3195, torqueNm: 4332 },
        { sizeInch: '24', sizeMm: 600, bolts: 20, boltDia: '1 ¼"', thread: 'UN8', stress: 45000, torqueFt: 5713, torqueNm: 7746 }
    ];

    const renderTorqueTable = (title, data, materials, gasketType) => (
        <Card sx={{ mb: 0, borderRadius: 0 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ fontSize: '1.1rem' }}>
                    {title}
                </Typography>
                <Box mb={1.5}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                        <strong>Flange Materials:</strong> {materials.flange}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                        <strong>Bolting Materials:</strong> {materials.bolting}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                        <strong>Gasket Type:</strong> {gasketType}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                        <strong>Bolt Lubricant:</strong> Molykote 1000 (µ = 0.11)
                    </Typography>
                </Box>
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'primary.main' }}>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.8rem', p: 1 }}>Size (Inch)</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.8rem', p: 1 }}>Size (mm)</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.8rem', p: 1 }}>No. of Bolt</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.8rem', p: 1 }}>Bolt Dia.</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.8rem', p: 1 }}>Thread</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.8rem', p: 1 }}>Bolt Stress (lb/in²)</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.8rem', p: 1 }}>Torque (lbf.ft.)</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.8rem', p: 1 }}>Torque (Nm)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map((row, index) => (
                                <TableRow key={index} hover>
                                    <TableCell sx={{ p: 1, fontSize: '0.85rem' }}>{row.sizeInch}</TableCell>
                                    <TableCell sx={{ p: 1, fontSize: '0.85rem' }}>{row.sizeMm}</TableCell>
                                    <TableCell sx={{ p: 1, fontSize: '0.85rem' }}>{row.bolts}</TableCell>
                                    <TableCell sx={{ p: 1, fontSize: '0.85rem' }}>{row.boltDia}</TableCell>
                                    <TableCell sx={{ p: 1, fontSize: '0.85rem' }}>{row.thread}</TableCell>
                                    <TableCell sx={{ p: 1, fontSize: '0.85rem' }}>{row.stress.toLocaleString()}</TableCell>
                                    <TableCell sx={{ p: 1, fontSize: '0.85rem' }}>{row.torqueFt}</TableCell>
                                    <TableCell sx={{ p: 1, fontSize: '0.85rem' }}>{row.torqueNm}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );

    return (
        <Box sx={{
            position: 'fixed',
            top: '60px',
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: 'white'
        }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton onClick={() => navigate('/main')} edge="start">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" fontWeight={600}>General Torque Procedure</Typography>
                </Box>
            </Box>

            {/* Scrollable Content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 0 }}>
                {/* Pre-checks Section */}
                <Card sx={{ mb: 0, borderRadius: 0 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="h6" gutterBottom color="primary" sx={{ fontSize: '1.1rem' }}>
                            Pre-checks for Bolt Tightening
                        </Typography>

                        <Divider sx={{ my: 1 }} />

                        <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ mt: 1.5 }}>
                            Flange Condition
                        </Typography>
                        <List dense disablePadding>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <ListItemText primary="Check conditions of flange faces for scratches, dirt, and scale." primaryTypographyProps={{ fontSize: '0.85rem' }} />
                            </ListItem>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <ListItemText primary="Check for corrosion pitting and tool marks." primaryTypographyProps={{ fontSize: '0.85rem' }} />
                            </ListItem>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <ListItemText primary="Inspect the gasket seating surfaces." primaryTypographyProps={{ fontSize: '0.85rem' }} />
                            </ListItem>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <ListItemText primary="Check the areas on the flange where the nuts will sit, it should be flat and free from pitting and excessive wear." primaryTypographyProps={{ fontSize: '0.85rem' }} />
                            </ListItem>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <ListItemText primary="RTJ Grooves must be kept clean, corrosion-free & undamaged." primaryTypographyProps={{ fontSize: '0.85rem' }} />
                            </ListItem>
                        </List>

                        <Divider sx={{ my: 1 }} />

                        <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ mt: 1.5 }}>
                            Flange Alignment Checks
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1, fontSize: '0.85rem' }}>
                            Visually examine the flange alignment to ensure that an acceptable fit has been obtained. While aligning flanges, make sure that there are no residual stresses in the joint. Using heat correction for flange alignment is not a good practice and should be strictly prohibited.
                        </Typography>
                        <List dense disablePadding>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <ListItemText primary="Flange faces should be parallel and aligned." primaryTypographyProps={{ fontSize: '0.85rem' }} />
                            </ListItem>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <ListItemText primary="The flange bolt holes should be in line so that the bolts will pass freely." primaryTypographyProps={{ fontSize: '0.85rem' }} />
                            </ListItem>
                        </List>

                        <Divider sx={{ my: 1 }} />

                        <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ mt: 1.5 }}>
                            Nut, Stud or Bolt Checks
                        </Typography>
                        <List dense disablePadding>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <ListItemText primary="Visually examine nuts & Stud/bolts before installation to assure they are free from defects such as corrosion, damaged threads, etc. Nut-bolts with damaged threads should not be used." primaryTypographyProps={{ fontSize: '0.85rem' }} />
                            </ListItem>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <ListItemText primary="Check the length of the stud or bolt to avoid short bolting and excessive threads. Flange bolts shall be furnished with insufficient length to allow the use of bolt tensioning equipment or spades, spacers, drip rings, and wafer valves, and the associated extra gaskets." primaryTypographyProps={{ fontSize: '0.85rem' }} />
                            </ListItem>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <ListItemText primary="Visually examine studs and nuts after cleaning to ensure they are free from burrs. Studs and nuts shall be cleaned using a wire brush to remove any dirt on the threads. Lubricant (MOLYKOTE) shall be applied on threads and nuts to flange contact surfaces. Lubricant shall not be used in the gasket and the gasket seating area." primaryTypographyProps={{ fontSize: '0.85rem' }} />
                            </ListItem>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <ListItemText primary="The bolt and nut material grades should be correctly identified before they are used." primaryTypographyProps={{ fontSize: '0.85rem' }} />
                            </ListItem>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <ListItemText primary="Bolts and nuts can only be reused if it is known that they have not been overloaded or exceeded their yield point." primaryTypographyProps={{ fontSize: '0.85rem' }} />
                            </ListItem>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <ListItemText primary="The nut identification marking must always point outwards when assembling the nut on the bolt." primaryTypographyProps={{ fontSize: '0.85rem' }} />
                            </ListItem>
                        </List>

                        <Divider sx={{ my: 1 }} />

                        <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ mt: 1.5 }}>
                            Gasket Checking
                        </Typography>
                        <List dense disablePadding>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <ListItemText primary="Do not use sealing compound, grease, or other paste or adhesive on gasket or flange faces." primaryTypographyProps={{ fontSize: '0.85rem' }} />
                            </ListItem>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <ListItemText primary="While gasket insertion, it shall not be forced into the gasket seat between the mating flange faces. Once the gasket is seated, the mating flanges are brought together carefully without shaking the gasket off the seat, installing all studs, and running up all nuts hand tight." primaryTypographyProps={{ fontSize: '0.85rem' }} />
                            </ListItem>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <ListItemText primary="Visually examine gaskets before installation to assure they are free from defects." primaryTypographyProps={{ fontSize: '0.85rem' }} />
                            </ListItem>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <ListItemText primary="Color coding shall be maintained as per the rate and type of gasket provided by the manufacturer." primaryTypographyProps={{ fontSize: '0.85rem' }} />
                            </ListItem>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <ListItemText primary="Clean gasket seating face using a wire brush." primaryTypographyProps={{ fontSize: '0.85rem' }} />
                            </ListItem>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <ListItemText primary="Make sure the material is as specified; look for any possible defects or damage in the gasket, such as folds or creases." primaryTypographyProps={{ fontSize: '0.85rem' }} />
                            </ListItem>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <ListItemText primary="All Soft material gaskets should be replaced with new ones whenever an opened joint is to be closed again." primaryTypographyProps={{ fontSize: '0.85rem' }} />
                            </ListItem>
                            <ListItem sx={{ py: 0.25, px: 0 }}>
                                <ListItemText primary="The spiral wound gasket shall be used only once." primaryTypographyProps={{ fontSize: '0.85rem' }} />
                            </ListItem>
                        </List>
                    </CardContent>
                </Card>

                {/* Torque Sequence Instructions */}
                <Card sx={{ mb: 0, borderRadius: 0 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="h6" gutterBottom color="primary" sx={{ fontSize: '1.1rem' }}>
                            Flange Bolt Torque Sequence
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1, fontSize: '0.85rem' }}>
                            Once all pre-checks are completed. You can go ahead with tightening the stud in the pre-defined torque sequence mentioned here. Torque bolts and nuts in a "CRISS-CROSS" sequence using a minimum of three torquing passes and the maximum bolt stress as defined.
                        </Typography>

                        <Box sx={{ bgcolor: '#e3f2fd', p: 1.5, borderRadius: 1, mb: 1 }}>
                            <Typography variant="body2" gutterBottom fontWeight="bold" sx={{ fontSize: '0.85rem' }}>
                                PASS 1 Torque Sequence:
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                Torque to a maximum of 30% of the final torque value in accordance with the torque sequence. Check that gasket is getting compressed uniformly.
                            </Typography>
                        </Box>

                        <Box sx={{ bgcolor: '#e3f2fd', p: 1.5, borderRadius: 1, mb: 1 }}>
                            <Typography variant="body2" gutterBottom fontWeight="bold" sx={{ fontSize: '0.85rem' }}>
                                PASS 2 Torque Sequence:
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                Torque to a maximum of 60% of the final torque value.
                            </Typography>
                        </Box>

                        <Box sx={{ bgcolor: '#e3f2fd', p: 1.5, borderRadius: 1, mb: 1 }}>
                            <Typography variant="body2" gutterBottom fontWeight="bold" sx={{ fontSize: '0.85rem' }}>
                                PASS 3 Torque Sequence:
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                Torque to the final torque value (100%).
                            </Typography>
                        </Box>

                        <Typography variant="body2" fontWeight="bold" color="error.main" sx={{ fontSize: '0.85rem' }}>
                            After the three basic torque passes are completed, repeat torquing the nuts at least once using the final torque in a "CRISS-CROSS" manner until no further rotation of the nut is observed.
                        </Typography>
                    </CardContent>
                </Card>

                {/* Bolt Pattern Diagrams */}
                <Card sx={{ mb: 0, borderRadius: 0 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="h6" gutterBottom color="primary" sx={{ fontSize: '1.1rem' }}>
                            Bolt Numbering Patterns
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1.5, fontSize: '0.85rem' }}>
                            For easy handling, bolt numbering shall be done clockwise around the flange with the following sequence.
                        </Typography>
                        <Grid container spacing={2}>
                            {[4, 8, 12, 16, 20, 24].map((numBolts) => (
                                <Grid item xs={12} sm={6} md={6} key={numBolts}>
                                    <BoltPatternDiagram numBolts={numBolts} size={350} />
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </Card>

                {/* Bolt Tightening Sequence Table */}
                <Card sx={{ mb: 0, borderRadius: 0 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="h6" gutterBottom color="primary" sx={{ fontSize: '1.1rem' }}>
                            Bolt Tightening Sequence Table
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1.5, fontSize: '0.85rem' }}>
                            The table below shows the torque sequence for flanges with 4 to 32 bolts.
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'primary.main' }}>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.8rem', p: 1 }}>Number of Bolt / Stud</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.8rem', p: 1 }}>Bolt Tightening Sequence to Follow</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {boltSequences.map((row) => (
                                        <TableRow key={row.bolts} hover>
                                            <TableCell sx={{ p: 1, fontSize: '0.85rem' }}>{row.bolts} bolt Flange</TableCell>
                                            <TableCell sx={{ p: 1, fontSize: '0.85rem', fontFamily: 'monospace' }}>{row.sequence}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>

                {/* Torque Tables */}
                {renderTorqueTable(
                    'Torque Table for Class 150 Flange',
                    class150Data,
                    {
                        flange: 'ASTM A105, ASTM A182 grades F50 and F51, ASTM A350 grades LF2 and LF3, ASTM A694 grade F52',
                        bolting: 'ASTM A193 grades B7 and B7M, ASTM A320 grades L7, L7M and L43.'
                    },
                    'Graphite Tanged Gasket'
                )}

                {renderTorqueTable(
                    'Torque Table for Class 300 Flange',
                    class300Data,
                    {
                        flange: 'ASTM A105, ASTM A182 grades F50 and F51, ASTM A350 grades LF2 and LF3, ASTM A694 grade F52',
                        bolting: 'ASTM A193 grades B7 and B7M, ASTM A320 grades L7, L7M and L43.'
                    },
                    'Spiral Wound and Ring Joint'
                )}

                {renderTorqueTable(
                    'Torque Table for Class 600 Flange',
                    class600Data,
                    {
                        flange: 'ASTM A105, ASTM A182 grades F50 and F51, ASTM A350 grades LF2 and LF3, ASTM A694 grades F52 and F60',
                        bolting: 'ASTM A193 grades B7 and B7M, ASTM A320 grades L7, L7M and L43.'
                    },
                    'Spiral Wound and Ring Joint'
                )}

                {renderTorqueTable(
                    'Torque Table for Class 900 Flange',
                    class900Data,
                    {
                        flange: 'ASTM A105, ASTM A182 grades F50 and F51, ASTM A350 grades LF2 and LF3, ASTM A694 grades F52, F60 and F65',
                        bolting: 'ASTM A193 grades B7 and B7M, ASTM A320 grades L7, L7M and L43.'
                    },
                    'Spiral Wound and Ring Joint'
                )}
            </Box>
        </Box>
    );
};

export default GeneralTorqueProcedure;
